import React, { useRef, useContext, useEffect } from 'react';
import { BrowserView, isMobile, isTablet } from 'react-device-detect';
import { makeStyles } from '@material-ui/core/styles';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import { gsap } from 'gsap';
import { Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import withWidth from '@material-ui/core/withWidth';
import AppContext from '../../contexts/AppContext';
import ImageFilterComp from './filters/ImageFilterComp';
import SceneContext from '../../contexts/SceneContext';
import DroppableContainerLayer from './filters/DroppableContainerLayer';
import ImageGrid from './filters/ImageGrid';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import UndoIcon from '@material-ui/icons/Undo';
import './EditLayer.css';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginBottom: 20,
  },
  list: {
    width: '50%',
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
  },
  fabRoot: {
    '& > *': {
      marginBottom: theme.spacing(2),
    },
  },
  filterContainer: {
    paddingTop: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    zIndex: 99,
    background: 'white',
    color: 'black',
    height: '100vh',
    width: '398px',
    position: 'fixed',
    overflow: 'scroll',
    top: '70px',
    left: '0px',
  },
  imagesContainer: {},

  imagesDropContainer: {
    flexGrow: 1,
    width: '340px',
    left: '-400px',
    position: 'fixed',
    overflow: 'auto',
    height: '100%', // why?
  },

  locationsContainer: {
    background: 'white',
    flexGrow: 1,
    width: '650px',
    position: 'fixed',
    overflow: 'auto',
    height: 'calc(100% - 60px)', // why?
    top: '70px',
    left: '0px',
  },

  drawer: {
    overflow: 'auto',
    height: '100%',
    background: 'transparent',
  },
  locationsHeader: {
    margin: theme.spacing(2),
  },
  iconHeader: {
    position: 'absolute',
    top: theme.spacing(3),
    right: theme.spacing(2),
  },
  gridContainer: {
    width: 'Opx',
    color: 'black',
  },
}));

const EditLayer = (props) => {
  const {
    addArtworkEnabled,
    artworksData,
    categories,
    currentLocation,
    startEditing,
    onCreateHandler,
    onUndoButtonHandler,
    onBackHomeHandler,
    onToggleArtworksList,
    onOpenHandler,
    onCloseHandler,
    showUndo,
    loadedGallery,
    openDefault,
  } = props;

  const classes = useStyles();

  const appContext = useContext(AppContext);
  const { dictionnary } = appContext;
  const ctx = React.useContext(SceneContext);

  const [open, setOpen] = React.useState(openDefault || false);
  const [mode, setMode] = React.useState('artworks');
  const [currentData, setCurrentData] = React.useState(artworksData);
  const [dataIndex, setDataIndex] = React.useState(1);
  const [interactive, setInteractive] = React.useState(startEditing);
  const [useDrag, setUseDrag] = React.useState(true);
  const drawerUI = useRef(null);
  const drawerList = useRef(null);
  const drawerUserList = useRef(null);

  const [filter, setFilters] = React.useState({
    minYear: 0,
    maxYear: 3000,
    ratioFilter: { portrait: true, landscape: true },
    colorIndex: -1,
    categories: [],
    curated: true,
    agora: true,
    gac: true,
  });

  const sendTrackEvent = (action, label, value) => {
    if (appContext.axl) {
      const AxL = require('../../utils/Axl').default;
      appContext.axl.sendMessage(AxL.ChildToHost.TRACK, {
        action,
        label,
        value,
      });
    }
  };

  const list = () => (
    <ImageGrid
      cols={1}
      onItemClickHandler={(result) => {
        // add image
        if (mode === 'artworks') {
          ctx.webgl.drawManager.addImage(result, {}, true);
          sendTrackEvent('artwork-selection', result.asset_id);
          onCloseHandler();
          resetData();
        }
      }}
      dragEnabled={useDrag}
      data={currentData}
      dataIndex={dataIndex}
    />
  );

  const DroppableContainer = React.forwardRef((props, ref) => (
    <DroppableContainerLayer
      onClickHandler={() => {
        onCloseHandler();

        resetData();
      }}
      onDropSuccess={(result) => {
        const event = {
          clientX: result.offsett.x,
          clientY: result.offsett.y,
        };

        const posInTexture = props.webgl.getMousePositionInTexture(event);
        props.webgl.drawManager.addImage(
          result.content.tile,
          posInTexture,
          true,
        );
        sendTrackEvent('artwork-selection', result.content.tile.asset_id);

        onCloseHandler();
        resetData();
      }}
    />
  ));

  const usePrevious = (value) => {
    const ref = useRef();

    useEffect(() => {
      ref.current = value;
    }, [value]); // Only re-run if value changes

    return ref.current;
  };

  const onColorChangeHandler = async (color) => {
    setFilters({
      ...filter,
      colorIndex: color.index,
    });
  };

  const onDataRangeChanged = async (range) => {
    setFilters({
      ...filter,
      minYear: range[0],
      maxYear: range[1],
    });
  };

  const onLandscapeChanged = async (ratioFilter) => {
    setFilters({
      ...filter,
      ratioFilter,
    });
  };

  const onCategoriesChanged = async (categories) => {
    setFilters({
      ...filter,
      categories,
    });
  };

  const onCuratedChanged = async (curated) => {
    setFilters({
      ...filter,
      curated: curated.unCurated,
    });
  };

  const onAgoraCollectionChanged = async (result) => {
    setFilters({
      ...filter,
      agora: result.agora,
    });
  };

  const onGacCollectionChanged = async (result) => {
    setFilters({
      ...filter,
      gac: result.gac,
    });
  };

  const resetData = () => {
    setFilters({
      minYear: 0,
      maxYear: 3000,
      ratioFilter: { portrait: true, landscape: true },
      colorIndex: -1,
      curated: true,
      agora: true,
      gac: true,
    });
  };

  const filterData = async () => {
    // randomize
    if (!currentData) return;
    appContext.dsManager.randomize(currentData);
    let data = [...appContext.dsManager.data];

    /*
    if (filter.curated) {
      data = await appContext.dsManager.filterByCurated(
        [...data],
        filter.curated,
      );
    }
    */

    let collections = [];
    if (filter.agora) collections.push('#TheWorldWeWant');
    if (filter.curated) collections.push('The Future is Unwritten');
    if (filter.gac) collections.push('Google Arts & Culture');

    data = await appContext.dsManager.filterByCollections(
      [...data],
      collections,
    );

    data = await appContext.dsManager.filterByLandscape(
      [...data],
      filter.ratioFilter,
    );

    if (filter.categories && filter.categories.length) {
      data = await appContext.dsManager.filterByCategories(
        [...data],
        filter.categories,
      );
    }

    /*
    data = await appContext.dsManager.filterByColorSimilarity(
      [...data],
      filter.colorIndex,
    );
    data = await appContext.dsManager.filter(
      [...data],
      filter.minYear,
      filter.maxYear,
    );
    */

    setCurrentData(data);
  };

  const onRandomize = () => {
    const data = appContext.dsManager.randomize([...currentData]);
    setCurrentData(data);
  };

  if (
    JSON.stringify(filter) !== JSON.stringify(usePrevious(filter)) ||
    currentData.length > 800 // TODO remvoe this! Hack for presentation
  ) {
    filterData();
  }

  React.useEffect(() => {
    if (open !== openDefault) setOpen(openDefault);

    if (mode === 'artworks') {
      if (open && drawerUI.current) {
        gsap.to(drawerUI.current, { left: 0, duration: 0.3 });
        gsap.to(drawerList.current, { left: 388, duration: 0.4 });
        if (drawerUserList.current !== null) {
          gsap.to(drawerUserList.current, { left: -350, duration: 0.5 });
        }
      }
    }

    const scrollableElement = drawerList.current;
    if (scrollableElement) {
      scrollableElement.addEventListener('scroll', (event) => {
        if (
          event.target.scrollTop ===
          event.target.scrollHeight - event.target.offsetHeight
        ) {
          setDataIndex(dataIndex + 1);
        }
      });
    }
  });

  const onAddArtworkPublic = () => {
    setMode('artworks');
    setCurrentData(artworksData);
    setUseDrag(true);
    onOpenHandler();
    props.onArtworksOpenHandler();
  };

  let content = null;
  const hasData = currentData && currentData.length > 0;
  if (mode === 'artworks') {
    content = (
      <>
        <Grid item xs={12}>
          <div className={classes.filterContainer}>
            <div className={'edit-close-btn'}>
              <IconButton
                aria-label="delete"
                onClick={() => {
                  onCloseHandler();
                  resetData();
                }}
              >
                <NavigateBeforeIcon fontSize="large" />
              </IconButton>
            </div>
            {open && (
              <ImageFilterComp
                nImages={currentData.length}
                categories={categories}
                onCategoriesChanged={(value) => onCategoriesChanged(value)}
                onColorChangeHandler={(value) => onColorChangeHandler(value)}
                onLandscapeChanged={(value) => onLandscapeChanged(value)}
                onDataRangeChanged={(range) => onDataRangeChanged(range)}
                onCuratedChanged={(range) => onCuratedChanged(range)}
                onAgoraCollectionChanged={(range) =>
                  onAgoraCollectionChanged(range)
                }
                onGacCollectionChanged={(range) =>
                  onGacCollectionChanged(range)
                }
                onRandomize={() => onRandomize()}
              />
            )}
            {!hasData && (
              <Typography>{dictionnary.get('edit-no-results')}</Typography>
            )}
          </div>
        </Grid>

        <Grid container>
          <Grid
            id="artworks-drawer-list"
            ref={drawerList}
            className={classes.imagesContainer}
            style={{ opacity: hasData ? 1 : 0.5 }}
            item
            xs={12}
          >
            {hasData ? list('left') : null}
          </Grid>

          <Grid item xs={4}>
            <DroppableContainer webgl={ctx.webgl} />
          </Grid>
        </Grid>
      </>
    );
  }

  let addArtworkButton = [];
  let addArtworkContent = addArtworkEnabled ? (
    <React.Fragment>
      <AddIcon />
      &nbsp; {dictionnary.get('edit-add-artwork')}
    </React.Fragment>
  ) : (
    <React.Fragment>
      {dictionnary.get('edit-add-artwork-disabled')}
    </React.Fragment>
  );

  addArtworkButton = (
    <>
      <Fab
        variant="extended"
        color="secondary"
        aria-label="add"
        onClick={onAddArtworkPublic}
        disabled={!addArtworkEnabled}
      >
        {addArtworkContent}
      </Fab>
    </>
  );

  let title = '';

  if (
    !loadedGallery ||
    !loadedGallery.meta ||
    !loadedGallery.meta.creator ||
    loadedGallery.meta.creator.length === 0
  ) {
    title =
      ' ' +
      dictionnary.get('common-in') +
      ' ' +
      currentLocation.city +
      ', ' +
      currentLocation.name;
  } else if (loadedGallery.meta && loadedGallery.meta.creator) {
    title =
      ' ' +
      dictionnary.get('common-createdby') +
      ' ' +
      loadedGallery.meta.creator +
      ' ' +
      dictionnary.get('common-in') +
      ' ' +
      currentLocation.city +
      ', ' +
      currentLocation.name;
  } else {
    title = currentLocation.city + ', ' + currentLocation.name;
  }

  return (
    <SceneContext.Consumer>
      {({ webgl }) => (
        <BrowserView>
          <div className="edit-layer">
            <div className="edit-create-container">
              <div className={classes.fabRoot}>
                {interactive && addArtworkButton}
              </div>
              <div className={classes.fabRoot}>
                {interactive && (
                  <Fab
                    variant="extended"
                    color="secondary"
                    aria-label="add"
                    className={'edit-undo-fab'}
                    disabled={!showUndo}
                    onClick={() => {
                      onUndoButtonHandler();
                    }}
                  >
                    <UndoIcon />
                  </Fab>
                )}
              </div>
            </div>
            <div className={classes.fabRoot}>
              {interactive && (
                <Fab
                  variant="extended"
                  color="secondary"
                  aria-label="add"
                  onClick={onBackHomeHandler}
                >
                  <LocationOnIcon />
                  &nbsp; {dictionnary.get('edit-create-gallery')}
                </Fab>
              )}
            </div>

            {!interactive && !isTablet && !isMobile && (
              <>
                <div className={classes.fabRoot}>
                  <Fab
                    variant="extended"
                    color="secondary"
                    aria-label="add"
                    onClick={() => {
                      setInteractive(true);
                      onCreateHandler();
                    }}
                  >
                    &nbsp; {dictionnary.get('edit-start')}
                  </Fab>
                </div>
                <div className={classes.fabRoot}>
                  <Fab
                    variant="extended"
                    color="secondary"
                    aria-label="add"
                    onClick={() => {
                      onBackHomeHandler();
                    }}
                  >
                    <LocationOnIcon />
                    &nbsp; {dictionnary.get('edit-change-location')}
                  </Fab>
                </div>
              </>
            )}

            <div className={classes.locationText} id={'container-details-flex'}>
              {currentLocation && (
                <>
                  <div
                    id={'container-details'}
                    style={{ width: 'max-content' }}
                  >
                    <Typography>
                      {loadedGallery && loadedGallery.meta && (
                        <>
                          <span>
                            <b> {loadedGallery.meta.theme}</b>
                            {title}
                          </span>
                          <br />
                        </>
                      )}
                      {!loadedGallery && (
                        <>
                          <span id="container-details-link">
                            {currentLocation.city}, {currentLocation.name}
                          </span>{' '}
                        </>
                      )}
                    </Typography>
                  </div>

                  {!isMobile &&
                    !isTablet &&
                    loadedGallery &&
                    loadedGallery.meta && (
                      <div
                        id={'container-details-artworks'}
                        onClick={() => {
                          onToggleArtworksList();
                        }}
                      >
                        {dictionnary.get('common-artworks-in')}
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
          {open && (
            <React.Fragment key="top">
              <div
                id="artworks-drawer"
                ref={drawerUI}
                className={classes.drawer}
              />
              <Grid className={classes.gridContainer} container spacing={3}>
                {content}
              </Grid>
            </React.Fragment>
          )}
        </BrowserView>
      )}
    </SceneContext.Consumer>
  );
};

export default withWidth()(EditLayer);
